import React from 'react';
import styled, { keyframes } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

interface IProps {
  children: React.ReactNode;
  repeat?: boolean;
  width?: string;
  height?: string;
  alignY?: 'top' | 'center' | 'bottom';
}

const pop = keyframes`
  0% { opacity: 0; transform: translateY(22px) scale(.65) rotate(-8deg); }
  55% { opacity: 1; transform: translateY(-12px) scale(1.08) rotate(5deg); }
  75% { transform: translateY(4px) scale(.97) rotate(-3deg); }
  100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); }
`;
const burst = keyframes`
  0% { opacity: 0; transform: translate(0, 0) rotate(0); }
  10% { opacity: 1; }
  75% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--travel-x), var(--travel-y)) rotate(var(--spin)); }
`;
const Wrapper = styled.div<{ $width: string; $height: string; $alignY: NonNullable<IProps['alignY']> }>`
  position: relative;
  display: flex;
  align-items: ${({ $alignY }) => ($alignY === 'bottom' ? 'flex-end' : $alignY === 'top' ? 'flex-start' : 'center')};
  justify-content: center;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  overflow: hidden;
`;
const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  animation: ${pop} 850ms ease-out both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const ConfettiItemContainer = styled.span<{ $repeat: boolean }>`
  position: absolute;
  width: 7px;
  height: 11px;
  pointer-events: none;
  opacity: 0;
  animation: ${burst} 1800ms cubic-bezier(0.12, 0.65, 0.4, 1) both;
  animation-iteration-count: ${({ $repeat }) => ($repeat ? 'infinite' : '1')};
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;
const CONFETTI_COLORS = ['#E9AC42', '#E77E76', '#64B9A2', '#79AFDB', '#B69AD6'];
const CONFETTI = Array.from({ length: 40 }, (_, index) => {
  const side = index % 2 === 0 ? 1 : -1;
  return {
    id: uuid4(),
    style: {
      left: side === 1 ? '12%' : '88%',
      top: `${52 + (index % 5) * 4}%`,
      backgroundColor: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
      borderRadius: index % 3 === 0 ? '50%' : '2px',
      animationDelay: `${(index % 8) * 45}ms`,
      '--travel-x': `${side * (25 + ((index * 37) % 190))}px`,
      '--travel-y': `${-65 - ((index * 29) % 160)}px`,
      '--spin': `${side * (180 + ((index * 47) % 540))}deg`,
    } as React.CSSProperties,
  };
});

const Celebration: React.FC<IProps> = ({
  children,
  repeat = false,
  width = 'fit-content',
  height = 'auto',
  alignY = 'center',
}) => {
  const ConfettiItems = CONFETTI.map((piece) => (
    <ConfettiItemContainer key={piece.id} style={piece.style} $repeat={repeat} />
  ));

  return (
    <Wrapper $width={width} $height={height} $alignY={alignY}>
      {ConfettiItems}
      <ContentContainer>{children}</ContentContainer>
    </Wrapper>
  );
};

export default Celebration;
